<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky fr-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ t("title") }}</span>
          <v-btn-toggle v-model="mode" mandatory class="as-modes">
            <v-btn v-for="item in modeOptions" :key="item.value" :value="item.value" flat small>
              {{ item.text }}
            </v-btn>
          </v-btn-toggle>
          <v-spacer></v-spacer>
          <template v-if="mode === 'summarized' && hasReport">
            <v-tooltip bottom>
              <v-btn slot="activator" icon small flat @click="expandAllBuckets">
                <v-icon small>unfold_more</v-icon>
              </v-btn>
              <span>{{ frT("expandAll") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <v-btn slot="activator" icon small flat @click="collapseAllBuckets">
                <v-icon small>unfold_less</v-icon>
              </v-btn>
              <span>{{ frT("collapseAll") }}</span>
            </v-tooltip>
          </template>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>{{ frT("exportExcel") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ frT("exportCsv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ frT("print") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="apply">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ frT("refresh") }}</span>
          </v-tooltip>
        </div>

        <div class="fin-filters">
          <template v-if="usesAccount">
            <account-picker
              v-model="filters.account_id"
              :label="t('account')"
              field-class="fin-field fin-field--wide"
              @change="onAccountRow"
            />
            <v-checkbox
              v-model="filters.include_children"
              :label="t('includeChildren')"
              hide-details
              class="fin-field as-check"
            ></v-checkbox>
          </template>

          <template v-if="mode === 'party'">
            <v-select
              v-model="filters.party_type"
              :items="partyTypeOptions"
              item-text="text"
              item-value="value"
              :label="t('partyType')"
              hide-details
              class="fin-field"
              @change="filters.party_id = null"
            ></v-select>
            <account-statement-party-picker
              :key="'party-' + filters.party_type"
              v-model="filters.party_id"
              :party-type="filters.party_type"
              :label="t('party')"
            />
          </template>

          <template v-if="mode === 'entry'">
            <v-text-field
              v-model="filters.reference_no"
              :label="t('entryNumber')"
              :hint="t('entryNumberHint')"
              hide-details
              clearable
              class="fin-field fin-field--wide"
              @keyup.enter="apply"
            ></v-text-field>
          </template>

          <template v-if="mode !== 'entry'">
            <v-text-field
              v-model="filters.from"
              type="date"
              :label="mode === 'comparative' ? t('periodAFrom') : frT('from')"
              hide-details
              class="fin-field"
            ></v-text-field>
            <v-text-field
              v-model="filters.to"
              type="date"
              :label="mode === 'comparative' ? t('periodATo') : frT('to')"
              hide-details
              class="fin-field"
            ></v-text-field>
          </template>

          <template v-if="mode === 'comparative'">
            <v-text-field
              v-model="filters.from_b"
              type="date"
              :label="t('periodBFrom')"
              hide-details
              class="fin-field"
            ></v-text-field>
            <v-text-field
              v-model="filters.to_b"
              type="date"
              :label="t('periodBTo')"
              hide-details
              class="fin-field"
            ></v-text-field>
          </template>

          <v-select
            v-if="mode === 'summarized'"
            v-model="filters.group_by"
            :items="groupByOptions"
            item-text="text"
            item-value="value"
            :label="t('groupBy')"
            hide-details
            class="fin-field"
          ></v-select>

          <v-select
            v-if="mode !== 'entry'"
            v-model="filters.currency"
            :items="currencyOptions"
            :label="frT('currency')"
            hide-details
            class="fin-field as-narrow"
          ></v-select>

          <template v-if="usesLines">
            <v-select
              v-model="filters.entry_type"
              :items="entryTypeOptions"
              item-text="text"
              item-value="value"
              :label="t('movementType')"
              hide-details
              class="fin-field"
            ></v-select>
            <v-text-field
              v-model="filters.amount_min"
              type="number"
              min="0"
              :label="t('amountMin')"
              hide-details
              clearable
              class="fin-field as-narrow"
            ></v-text-field>
            <v-text-field
              v-model="filters.amount_max"
              type="number"
              min="0"
              :label="t('amountMax')"
              hide-details
              clearable
              class="fin-field as-narrow"
            ></v-text-field>
            <v-text-field
              v-model="filters.search"
              :label="t('search')"
              hide-details
              clearable
              class="fin-field"
              @keyup.enter="apply"
            ></v-text-field>
            <v-select
              v-model="filters.per_page"
              :items="perPageOptions"
              item-text="text"
              item-value="value"
              :label="frT('perPage')"
              hide-details
              class="fin-field as-narrow"
            ></v-select>
          </template>

          <v-btn small color="primary" @click="apply">{{ frT("apply") }}</v-btn>
        </div>
      </div>

      <div v-if="!hasReport && !loading" class="as-hint">{{ missingInputText || frT("noData") }}</div>

      <template v-if="hasReport">
        <!-- Header block (her p11): account / party / entry identity -->
        <div class="as-info">
          <div v-for="pair in infoPairs" :key="pair[0]" class="as-info-item">
            <span class="as-info-label">{{ pair[0] }}</span>
            <span class="as-info-value">{{ pair[1] }}</span>
          </div>
        </div>

        <div class="as-badges fr-no-print">
          <v-chip v-if="report.provisional && report.provisional.is_provisional" small color="amber darken-2" text-color="white">
            <v-icon small class="me-1">hourglass_empty</v-icon>
            {{ report.provisional.label }}: {{ report.provisional.months.join("، ") }}
          </v-chip>
          <v-chip
            v-if="report.reconciliation"
            small
            :color="report.reconciliation.matched ? 'green' : 'red'"
            text-color="white"
            :title="reconciliationTitle(report.reconciliation)"
          >
            <v-icon small class="me-1">{{ report.reconciliation.matched ? "check_circle" : "warning" }}</v-icon>
            {{ t("reconciliation") }}: {{ report.reconciliation.label }}
            <span v-if="!report.reconciliation.matched">
              &nbsp;({{ t("reconciliationDifference") }} {{ money(report.reconciliation.difference) }} - {{ t("reconciliationLedger") }}
              {{ money(report.reconciliation.ledger_balance) }})
            </span>
          </v-chip>
          <v-chip v-if="report.filtered" small color="blue-grey" text-color="white">
            <v-icon small class="me-1">filter_list</v-icon>
            {{ t("filteredNotice") }}
          </v-chip>
        </div>

        <div v-for="(alert, i) in report.alerts || []" :key="'a' + i" class="fin-alert-error as-alert">
          <v-icon small color="#c62828">warning</v-icon>
          {{ alert.text }}
        </div>
        <div v-for="(warning, i) in report.warnings || []" :key="'w' + i" class="as-warning">
          <v-icon small color="deep-orange">info</v-icon>
          {{ warning.text }}
        </div>
        <div v-if="report.too_many" class="fin-alert-error as-alert">
          <v-icon small color="#c62828">warning</v-icon>
          {{ frT("tooManyRows") }}
        </div>

        <!-- party: per-account balances -->
        <div v-if="mode === 'party'" class="fin-table-wrap as-table-wrap as-table-wrap--short">
          <table class="as-table fin-table">
            <thead>
              <tr>
                <th>{{ frT("accountCode") }}</th>
                <th>{{ frT("accountName") }}</th>
                <th>{{ t("accountKind") }}</th>
                <th class="fin-num-head">{{ t("opening") }}</th>
                <th class="fin-num-head">{{ frT("debit") }}</th>
                <th class="fin-num-head">{{ frT("credit") }}</th>
                <th class="fin-num-head">{{ t("closing") }}</th>
                <th>{{ t("colNature") }}</th>
                <th>{{ t("colNotes") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in report.accounts" :key="account.id" :class="{ 'as-abnormal': account.is_abnormal }">
                <td class="fin-code">{{ account.code }}</td>
                <td>{{ account.name }}</td>
                <td>{{ account.purpose_label }}<span v-if="account.is_shared" class="as-muted"> ({{ t("sharedAccount") }})</span></td>
                <td class="fin-num">{{ money(account.opening) }}</td>
                <td class="fin-num">{{ money(account.debit) }}</td>
                <td class="fin-num">{{ money(account.credit) }}</td>
                <td class="fin-num">{{ money(account.closing) }}</td>
                <td :class="sideClass(account.side)">{{ account.nature_label }}</td>
                <td>{{ account.alert ? account.alert.text : "" }}</td>
              </tr>
              <tr v-if="!report.accounts.length">
                <td colspan="9" class="fin-empty">{{ t("noPartyAccounts") }}</td>
              </tr>
            </tbody>
            <tfoot v-if="report.accounts.length">
              <tr class="as-total">
                <td></td>
                <td>{{ t("partyTotal") }}</td>
                <td></td>
                <td class="fin-num">{{ money(report.opening.balance) }}</td>
                <td class="fin-num">{{ money(report.totals.debit) }}</td>
                <td class="fin-num">{{ money(report.totals.credit) }}</td>
                <td class="fin-num">{{ money(report.closing.balance) }}</td>
                <td>{{ report.closing.nature_label }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- detailed / party: lines with the running balance -->
        <div v-if="usesLines" class="fin-table-wrap as-table-wrap">
          <table class="as-table fin-table">
            <thead>
              <tr>
                <th v-for="column in lineColumns" :key="column.key" :class="{ 'fin-num-head': isNumeric(column) }">{{ column.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr class="as-opening">
                <td :colspan="labelSpan">{{ pageOpeningLabel }}</td>
                <td class="fin-num"></td>
                <td class="fin-num"></td>
                <td class="fin-num">{{ money(report.page_opening_balance) }}</td>
                <td>{{ natureLabelOf(report.page_opening_balance) }}</td>
                <td></td>
              </tr>
              <tr v-for="row in report.rows" :key="row.line_id" :class="lineRowClasses(row)">
                <td class="fin-num">{{ row.index }}</td>
                <td class="as-nowrap">{{ row.date }}</td>
                <td class="fin-code">
                  <a class="as-link" @click="openEntry(row)">{{ row.reference_no || "#" + row.journal_entry_id }}</a>
                </td>
                <td v-if="showAccountColumn" class="as-nowrap">
                  <span class="fin-code">{{ row.account_code }}</span> {{ row.account_name }}
                </td>
                <td class="as-desc">{{ row.description || "—" }}</td>
                <td class="as-nowrap">{{ row.reference || "—" }}</td>
                <td class="fin-num">{{ money(row.debit) }}</td>
                <td class="fin-num">{{ money(row.credit) }}</td>
                <td class="fin-num">{{ money(row.balance) }}</td>
                <td :class="sideClass(row.side)">{{ row.nature_label }}</td>
                <td class="as-flags">
                  <span v-for="flag in row.flags" :key="flag.code" class="as-flag" :class="'as-flag-' + flag.code">{{ flag.text }}</span>
                </td>
              </tr>
              <tr v-if="!report.rows.length">
                <td :colspan="lineColumns.length" class="fin-empty">{{ report.too_many ? frT("tooManyRows") : report.message || frT("noMovements") }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr v-if="report.filtered && report.filtered_totals" class="as-subtotal">
                <td :colspan="labelSpan">{{ t("filteredTotal") }} ({{ report.filtered_totals.lines }})</td>
                <td class="fin-num">{{ money(report.filtered_totals.debit) }}</td>
                <td class="fin-num">{{ money(report.filtered_totals.credit) }}</td>
                <td colspan="3"></td>
              </tr>
              <tr class="as-total">
                <td :colspan="labelSpan">{{ t("periodTotal") }} ({{ report.totals.lines }} {{ t("linesWord") }})</td>
                <td class="fin-num">{{ money(report.totals.debit) }}</td>
                <td class="fin-num">{{ money(report.totals.credit) }}</td>
                <td colspan="3"></td>
              </tr>
              <tr class="as-closing">
                <td :colspan="labelSpan">{{ t("closing") }}</td>
                <td class="fin-num"></td>
                <td class="fin-num"></td>
                <td class="fin-num">{{ money(report.closing.balance) }}</td>
                <td :class="sideClass(report.closing.side)">{{ report.closing.nature_label }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-if="usesLines && lastPage > 1" class="as-pager fr-no-print">
          <v-pagination v-model="filters.page" :length="lastPage" :total-visible="9" @input="loadReport"></v-pagination>
          <span class="as-muted">{{ t("rowsTotal") }}: {{ report.pagination.total }}</span>
        </div>

        <!-- summarized -->
        <div v-if="mode === 'summarized'" class="fin-table-wrap as-table-wrap">
          <table class="as-table fin-table">
            <thead>
              <tr>
                <th class="as-toggle-col fr-no-print"></th>
                <th>{{ report.group_by === "week" ? t("week") : t("month") }}</th>
                <th class="fin-num-head">{{ t("movementsCount") }}</th>
                <th class="fin-num-head">{{ t("totalDebit") }}</th>
                <th class="fin-num-head">{{ t("totalCredit") }}</th>
                <th class="fin-num-head">{{ t("netMovement") }}</th>
                <th class="fin-num-head">{{ frT("balance") }}</th>
                <th>{{ t("colNature") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr class="as-opening">
                <td class="fr-no-print"></td>
                <td>{{ t("opening") }} ({{ report.header.period.from }})</td>
                <td colspan="4"></td>
                <td class="fin-num">{{ money(report.opening.balance) }}</td>
                <td>{{ report.opening.nature_label }}</td>
              </tr>
              <template v-for="bucket in report.buckets">
                <tr :key="'b' + bucket.key" :class="{ 'as-abnormal': bucket.is_abnormal, 'as-bucket-empty': !bucket.lines }">
                  <td class="as-toggle-col fr-no-print">
                    <v-btn v-if="bucket.lines" icon small class="as-toggle" @click="toggleBucket(bucket)">
                      <v-icon small>{{ expandedBuckets[bucket.key] ? "remove" : "add" }}</v-icon>
                    </v-btn>
                  </td>
                  <td class="as-nowrap">
                    {{ bucket.label }}
                    <span v-if="bucket.provisional" class="as-flag as-flag-provisional">{{ t("provisional") }}</span>
                  </td>
                  <td class="fin-num">{{ bucket.lines }}</td>
                  <td class="fin-num">{{ money(bucket.debit) }}</td>
                  <td class="fin-num">{{ money(bucket.credit) }}</td>
                  <td class="fin-num">{{ signed(bucket.net) }}</td>
                  <td class="fin-num">{{ money(bucket.balance) }}</td>
                  <td :class="sideClass(bucket.side)">{{ bucket.nature_label }}</td>
                </tr>
                <tr v-if="expandedBuckets[bucket.key]" :key="'d' + bucket.key" class="as-drill-row">
                  <td class="fr-no-print"></td>
                  <td colspan="7">
                    <div v-if="expandedBuckets[bucket.key].loading" class="as-muted">{{ t("loadingLines") }}</div>
                    <div v-else-if="expandedBuckets[bucket.key].error" class="as-muted">{{ expandedBuckets[bucket.key].error }}</div>
                    <table v-else class="as-table as-inner fin-table">
                      <thead>
                        <tr>
                          <th>{{ t("colDate") }}</th>
                          <th>{{ t("colEntryNo") }}</th>
                          <th>{{ t("colDescription") }}</th>
                          <th>{{ t("colReference") }}</th>
                          <th class="fin-num-head">{{ frT("debit") }}</th>
                          <th class="fin-num-head">{{ frT("credit") }}</th>
                          <th class="fin-num-head">{{ frT("balance") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in expandedBuckets[bucket.key].rows" :key="row.line_id">
                          <td class="as-nowrap">{{ row.date }}</td>
                          <td class="fin-code"><a class="as-link" @click="openEntry(row)">{{ row.reference_no || "#" + row.journal_entry_id }}</a></td>
                          <td class="as-desc">{{ row.description || "—" }}</td>
                          <td class="as-nowrap">{{ row.reference || "—" }}</td>
                          <td class="fin-num">{{ money(row.debit) }}</td>
                          <td class="fin-num">{{ money(row.credit) }}</td>
                          <td class="fin-num">{{ money(row.balance) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="as-total">
                <td class="fr-no-print"></td>
                <td>{{ frT("total") }}</td>
                <td class="fin-num">{{ report.totals.lines }}</td>
                <td class="fin-num">{{ money(report.totals.debit) }}</td>
                <td class="fin-num">{{ money(report.totals.credit) }}</td>
                <td class="fin-num">{{ signed(report.totals.net) }}</td>
                <td colspan="2"></td>
              </tr>
              <tr class="as-closing">
                <td class="fr-no-print"></td>
                <td>{{ t("closing") }} ({{ report.header.period.to }})</td>
                <td colspan="4"></td>
                <td class="fin-num">{{ money(report.closing.balance) }}</td>
                <td :class="sideClass(report.closing.side)">{{ report.closing.nature_label }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- entry -->
        <div v-if="mode === 'entry'" class="fin-table-wrap as-table-wrap">
          <div v-if="report.flags && report.flags.length" class="as-badges">
            <span v-for="flag in report.flags" :key="flag.code" class="as-flag" :class="'as-flag-' + flag.code">{{ flag.text }}</span>
          </div>
          <table class="as-table fin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ frT("accountCode") }}</th>
                <th>{{ frT("accountName") }}</th>
                <th class="fin-num-head">{{ frT("debit") }}</th>
                <th class="fin-num-head">{{ frT("credit") }}</th>
                <th>{{ t("colDescription") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in report.lines" :key="line.line_id">
                <td class="fin-num">{{ line.index }}</td>
                <td class="fin-code">
                  <a class="as-link" @click="openAccount(line)">{{ line.account_code }}</a>
                </td>
                <td>{{ line.account_name }}</td>
                <td class="fin-num">{{ money(line.debit) }}</td>
                <td class="fin-num">{{ money(line.credit) }}</td>
                <td class="as-desc">{{ line.line_memo || "—" }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="as-total" :class="report.totals.is_balanced ? 'as-ok' : 'as-bad'">
                <td></td>
                <td></td>
                <td>{{ frT("total") }}</td>
                <td class="fin-num">{{ money(report.totals.debit) }}</td>
                <td class="fin-num">{{ money(report.totals.credit) }}</td>
                <td>
                  <v-icon small :color="report.totals.is_balanced ? 'green' : 'red'">
                    {{ report.totals.is_balanced ? "check_circle" : "error" }}
                  </v-icon>
                  {{ report.totals.balance_label }}
                  <span v-if="!report.totals.is_balanced">({{ money(report.totals.difference) }})</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- comparative -->
        <div v-if="mode === 'comparative'" class="fin-table-wrap as-table-wrap">
          <table class="as-table fin-table">
            <thead>
              <tr>
                <th>{{ t("item") }}</th>
                <th class="fin-num-head">{{ periodALabel }}</th>
                <th class="fin-num-head">{{ periodBLabel }}</th>
                <th class="fin-num-head">{{ t("difference") }}</th>
                <th class="fin-num-head">{{ t("changePct") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in report.items" :key="item.key" :class="{ 'as-opening': item.key === 'opening' || item.key === 'closing' }">
                <td>{{ item.label }}</td>
                <td class="fin-num">{{ comparativeValue(item, item.a) }}</td>
                <td class="fin-num">{{ comparativeValue(item, item.b) }}</td>
                <td class="fin-num">{{ comparativeValue(item, item.difference, true) }}</td>
                <td class="fin-num">{{ pct(item.change_pct) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="(report.notes || []).length" class="as-notes">
          <div v-for="(note, i) in report.notes" :key="'n' + i" class="as-note">{{ note }}</div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
/*
 * Report 2.5 of the accountant's document 2026-10-03 (account-statement):
 * كشف حساب مفصّل in five modes -- detailed (running balance, 50 per page or
 * all up to 10,000), summarized (month / Saturday-first week, each bucket
 * expands into its lines), party (captain / centre / business / owner, all
 * mapped leaves + the captain's lines on the shared 21133), entry (one
 * journal entry with its balance check) and comparative (two periods).
 *
 * API: this.api.ACTIONS.ACCOUNTING_ACCOUNT_STATEMENT (GET, format=json|csv).
 * Money in the payload is STORED (old) units: display through money() /
 * the export mixin (÷100); the amount filter is typed in display units and
 * sent through toStoredAmount().
 *
 * Deep link: /accounting/reports/account-statement?mode=entry&reference_no=JE-1
 * or ?account_id=12&from=2026-09-01&to=2026-09-30 (any filter key).
 */
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import AccountPicker from "@/components/finance/AccountPicker";
import AccountStatementPartyPicker from "@/components/finance/AccountStatementPartyPicker";
import financeReportExport from "@/mixins/financeReportExport";
import { formatFinanceDate } from "@/mixins/financeDate";
import { companyNow } from "@/util/companyTime";

const MODES = ["detailed", "summarized", "party", "entry", "comparative"];
const FILTER_KEYS = [
  "account_id",
  "include_children",
  "from",
  "to",
  "from_b",
  "to_b",
  "currency",
  "group_by",
  "party_type",
  "party_id",
  "reference_no",
  "per_page",
  "entry_type",
  "search",
];

export default {
  name: "AccountStatement",
  title() {
    return this.$t("finance.reports2.accountStatement.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent, AccountPicker, AccountStatementPartyPicker },
  mixins: [financeReportExport],
  data() {
    // Final-version doc 2026-10-05: the default period is built from the
    // company (Damascus) day on the server-synced clock, not the PC calendar.
    const now = companyNow();
    const monthStart = now.clone().startOf("month");
    const prevStart = now.clone().subtract(1, "month").startOf("month");
    const prevEnd = now.clone().startOf("month").subtract(1, "day");

    return {
      loading: false,
      report: null,
      mode: "detailed",
      accountRow: null,
      expandedBuckets: {},
      filters: {
        account_id: null,
        include_children: false,
        from: monthStart.format("YYYY-MM-DD"),
        to: now.format("YYYY-MM-DD"),
        from_b: prevStart.format("YYYY-MM-DD"),
        to_b: prevEnd.format("YYYY-MM-DD"),
        currency: "SYP",
        group_by: "month",
        party_type: "captain",
        party_id: null,
        reference_no: "",
        page: 1,
        per_page: "50",
        entry_type: null,
        amount_min: null,
        amount_max: null,
        search: "",
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.mode === this.mode);
    },
    usesAccount() {
      return ["detailed", "summarized", "comparative"].indexOf(this.mode) !== -1;
    },
    usesLines() {
      return this.mode === "detailed" || this.mode === "party";
    },
    modeOptions() {
      return MODES.map((value) => ({ value: value, text: this.t("mode_" + value) }));
    },
    partyTypeOptions() {
      return ["captain", "center", "business", "owner"].map((value) => ({ value: value, text: this.t("party_" + value) }));
    },
    groupByOptions() {
      return [
        { value: "month", text: this.t("month") },
        { value: "week", text: this.t("week") },
      ];
    },
    entryTypeOptions() {
      return [
        { value: null, text: this.t("allMovements") },
        { value: "debit", text: this.frT("debit") },
        { value: "credit", text: this.frT("credit") },
      ];
    },
    perPageOptions() {
      return [
        { value: "25", text: "25" },
        { value: "50", text: "50" },
        { value: "100", text: "100" },
        { value: "200", text: "200" },
        { value: "all", text: this.frT("showAll") },
      ];
    },
    currencyOptions() {
      const list = (this.report && this.report.currencies) || [];
      const codes = list.length ? list.slice() : ["SYP"];
      if (codes.indexOf(this.filters.currency) === -1) {
        codes.push(this.filters.currency);
      }
      return codes;
    },
    missingInputText() {
      if (this.usesAccount && !this.filters.account_id) return this.t("pickAccount");
      if (this.mode === "party" && !this.filters.party_id) return this.t("pickParty");
      if (this.mode === "entry" && !this.filters.reference_no) return this.t("pickEntry");
      return "";
    },
    reportNature() {
      if (!this.report) return "debit";
      if (this.report.party) return this.report.party.nature;
      if (this.report.account) return this.report.account.normal_balance;
      return "debit";
    },
    showAccountColumn() {
      return !!(this.report && (this.report.mode === "party" || (this.report.account && this.report.account.include_children)));
    },
    lineColumns() {
      return this.buildLineColumns(this.showAccountColumn);
    },
    labelSpan() {
      // Columns before debit: #, date, entry no, [account], description, reference.
      return this.showAccountColumn ? 6 : 5;
    },
    lastPage() {
      return (this.report && this.report.pagination && this.report.pagination.last_page) || 1;
    },
    pageOpeningLabel() {
      if (!this.report) return "";
      const firstPage = !this.report.pagination || this.report.pagination.page === 1;
      return firstPage && !this.report.filtered ? this.t("opening") : this.t("carriedForward");
    },
    periodALabel() {
      const p = (this.report && this.report.periods && this.report.periods.a) || {};
      return this.t("periodA") + " " + (p.from || "") + " → " + (p.to || "");
    },
    periodBLabel() {
      const p = (this.report && this.report.periods && this.report.periods.b) || {};
      return this.t("periodB") + " " + (p.from || "") + " → " + (p.to || "");
    },
    infoPairs() {
      return this.buildInfoPairs();
    },
  },
  watch: {
    mode() {
      this.report = null;
      this.expandedBuckets = {};
      this.filters.page = 1;
      if (!this.missingInputText) {
        this.loadReport();
      }
    },
  },
  mounted() {
    // A mode taken from the URL loads through the mode watcher.
    const modeChanged = this.applyRouteQuery();
    if (!modeChanged && !this.missingInputText) {
      this.loadReport();
    }
  },
  methods: {
    t(key) {
      return this.$t("finance.reports2.accountStatement." + key);
    },
    money(value) {
      return this.formatMoney(value);
    },
    // The «المطابقة» control: the statement's closing balance against an
    // independent ledger sum (backend), with the difference when they differ.
    reconciliationTitle(rec) {
      const head = this.t("reconciliation") + ": " + rec.label;
      if (rec.matched) {
        return head;
      }
      return (
        head +
        " - " +
        this.t("reconciliationDifference") +
        " " +
        this.money(rec.difference) +
        " - " +
        this.t("reconciliationLedger") +
        " " +
        this.money(rec.ledger_balance)
      );
    },
    signed(value) {
      const n = Number(value) || 0;
      return (n > 0 ? "+" : "") + this.formatMoney(n);
    },
    pct(value) {
      if (value === null || value === undefined) return "—";
      const n = Number(value);
      return (n > 0 ? "+" : "") + n.toFixed(2) + "%";
    },
    isNumeric(column) {
      return ["money", "number", "count", "percent"].indexOf(column.type) !== -1;
    },
    comparativeValue(item, value, withSign) {
      if (item.key === "lines") {
        const n = Number(value) || 0;
        return (withSign && n > 0 ? "+" : "") + n;
      }
      return withSign ? this.signed(value) : this.money(value);
    },
    natureLabelOf(balance) {
      const n = Math.round((Number(balance) || 0) * 100) / 100;
      const nature = this.reportNature;
      if (Math.abs(n) < 0.005) return this.frT("balanced");
      if (n > 0) return this.frT(nature);
      return this.frT(nature === "debit" ? "credit" : "debit") + " " + this.t("abnormalSuffix");
    },
    sideClass(side) {
      return "as-side as-side-" + (side || "balanced");
    },
    lineRowClasses(row) {
      return {
        "as-abnormal": row.is_abnormal,
        "as-reversal": row.is_reversal || row.is_reversed,
      };
    },
    applyRouteQuery() {
      const query = (this.$route && this.$route.query) || {};
      let modeChanged = false;
      if (query.mode && MODES.indexOf(query.mode) !== -1 && query.mode !== this.mode) {
        this.mode = query.mode;
        modeChanged = true;
      }
      FILTER_KEYS.forEach((key) => {
        if (query[key] === undefined || query[key] === null || query[key] === "") return;
        if (key === "account_id" || key === "party_id") {
          this.filters[key] = Number(query[key]) || null;
        } else if (key === "include_children") {
          this.filters[key] = query[key] === "1" || query[key] === "true";
        } else {
          this.filters[key] = String(query[key]);
        }
      });
      return modeChanged;
    },
    onAccountRow(row) {
      this.accountRow = row;
      if (row && row.has_children) {
        this.filters.include_children = true;
      }
    },
    buildParams(mode, extra) {
      const f = this.filters;
      const params = { mode: mode };
      if (mode === "entry") {
        params.reference_no = (f.reference_no || "").trim();
        return Object.assign(params, extra || {});
      }
      params.from = f.from;
      params.to = f.to;
      if (f.currency) params.currency = f.currency;
      if (mode === "party") {
        params.party_type = f.party_type;
        params.party_id = f.party_id;
      } else {
        params.account_id = f.account_id;
        params.include_children = f.include_children ? 1 : 0;
      }
      if (mode === "summarized") params.group_by = f.group_by;
      if (mode === "comparative") {
        params.from_b = f.from_b;
        params.to_b = f.to_b;
      }
      if (mode === "detailed" || mode === "party") {
        params.page = f.page;
        params.per_page = f.per_page;
        if (f.entry_type) params.entry_type = f.entry_type;
        const min = this.toStoredAmount(f.amount_min);
        const max = this.toStoredAmount(f.amount_max);
        if (min !== null) params.amount_min = min;
        if (max !== null) params.amount_max = max;
        if (f.search) params.search = f.search;
      }
      return Object.assign(params, extra || {});
    },
    apply() {
      this.filters.page = 1;
      this.loadReport();
    },
    loadReport() {
      if (this.missingInputText) {
        window.getApp.$emit("SHOW_TOAST", this.missingInputText, "orange");
        return;
      }
      const mode = this.mode;
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_ACCOUNT_STATEMENT, { params: this.buildParams(mode) })
        .then((response) => {
          if (mode !== this.mode) return;
          this.report = (response.data && response.data.data) || null;
          this.expandedBuckets = {};
          if (this.report && this.report.pagination && this.report.pagination.page) {
            this.filters.page = this.report.pagination.page;
          }
        })
        .catch((error) => {
          if (mode === this.mode) this.report = null;
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.frT("failedLoad")), "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** Lines of one summarized bucket (detailed mode over the bucket's window, all rows). */
    fetchBucket(bucket) {
      const next = Object.assign({}, this.expandedBuckets);
      next[bucket.key] = { loading: true, rows: [], error: "" };
      this.expandedBuckets = next;
      const params = this.buildParams("detailed", { from: bucket.from, to: bucket.to, per_page: "all", page: 1 });
      // The row filters belong to the detailed tab (hidden here): a bucket
      // always expands into ALL of its lines.
      ["group_by", "entry_type", "amount_min", "amount_max", "search"].forEach((key) => delete params[key]);
      return this.axios
        .get(this.api.ACTIONS.ACCOUNTING_ACCOUNT_STATEMENT, { params: params })
        .then((response) => {
          const data = (response.data && response.data.data) || {};
          this.setBucket(bucket.key, { loading: false, rows: data.rows || [], error: data.too_many ? this.frT("tooManyRows") : "" });
        })
        .catch((error) => {
          this.setBucket(bucket.key, { loading: false, rows: [], error: this.frErrorMessage(error, this.frT("failedLoad")) });
        });
    },
    setBucket(key, value) {
      if (!this.expandedBuckets[key]) return; // collapsed meanwhile
      const next = Object.assign({}, this.expandedBuckets);
      next[key] = value;
      this.expandedBuckets = next;
    },
    toggleBucket(bucket) {
      if (this.expandedBuckets[bucket.key]) {
        const next = Object.assign({}, this.expandedBuckets);
        delete next[bucket.key];
        this.expandedBuckets = next;
        return;
      }
      this.fetchBucket(bucket);
    },
    expandAllBuckets() {
      ((this.report && this.report.buckets) || []).forEach((bucket) => {
        if (bucket.lines && !this.expandedBuckets[bucket.key]) {
          this.fetchBucket(bucket);
        }
      });
    },
    collapseAllBuckets() {
      this.expandedBuckets = {};
    },
    openEntry(row) {
      this.filters.reference_no = row.reference_no || String(row.journal_entry_id);
      if (this.mode === "entry") {
        this.loadReport();
      } else {
        this.mode = "entry"; // the mode watcher loads it
      }
    },
    openAccount(line) {
      this.filters.account_id = line.gl_account_id;
      this.filters.include_children = false;
      if (this.report && this.report.entry && this.report.entry.date) {
        // Keep the user's window when it already covers the entry's date.
        const date = this.report.entry.date;
        if (!(this.filters.from <= date && date <= this.filters.to)) {
          this.filters.from = date.slice(0, 8) + "01";
          this.filters.to = date;
        }
      }
      this.filters.page = 1;
      this.mode = "detailed";
    },

    // ------------------------------------------------------------------
    // Header / info block
    // ------------------------------------------------------------------
    buildInfoPairs() {
      const r = this.report;
      if (!r) return [];
      const h = r.header || {};
      const pairs = [];
      if (r.account) {
        pairs.push([this.frT("accountCode"), r.account.code]);
        pairs.push([this.frT("accountName"), r.account.name]);
        pairs.push([this.t("level"), String(r.account.level)]);
        pairs.push([this.t("parentAccount"), r.account.parent_label]);
        pairs.push([this.t("normalBalance"), r.account.nature_label]);
        if (r.account.include_children) {
          pairs.push([this.t("includeChildren"), this.t("yes") + " (" + r.account.accounts_count + ")"]);
        }
      }
      if (r.party) {
        pairs.push([this.t("partyType"), r.party.type_label]);
        pairs.push([this.t("party"), r.party.name + " #" + r.party.id]);
        pairs.push([this.t("normalBalance"), r.party.nature_label]);
      }
      if (r.entry) {
        const e = r.entry;
        pairs.push([this.t("entryNumber"), e.reference_no || "#" + e.id]);
        pairs.push([this.t("colDate"), e.journal_date]);
        pairs.push([this.t("colDescription"), e.memo || "—"]);
        pairs.push([this.t("source"), e.source || "—"]);
        pairs.push([this.t("event"), e.event_key || "—"]);
        if (e.operation_ref) pairs.push([this.t("colReference"), e.operation_ref]);
        if (e.booking_id) pairs.push([this.t("booking"), "#" + e.booking_id]);
        if (e.captain_id) pairs.push([this.t("party_captain"), "#" + e.captain_id]);
        pairs.push([this.t("createdBy"), e.created_by_name || (e.created_by ? "#" + e.created_by : this.t("system"))]);
        // Final-version doc 2026-10-05: company time even if a zoned value arrives.
        pairs.push([this.t("createdAt"), e.created_at ? formatFinanceDate(e.created_at, true) : "—"]);
        if (e.reverse_of) pairs.push([this.t("reverseOf"), e.reverse_of.reference_no || "#" + e.reverse_of.id]);
        if (e.reversed_by) pairs.push([this.t("reversedBy"), e.reversed_by.reference_no || "#" + e.reversed_by.id]);
      }
      if (r.mode === "comparative" && r.periods) {
        pairs.push([this.t("periodA"), r.periods.a.from + " → " + r.periods.a.to]);
        pairs.push([this.t("periodB"), r.periods.b.from + " → " + r.periods.b.to]);
      } else if (h.period && !r.entry) {
        pairs.push([this.frT("period"), this.frPeriodText(h.period)]);
      }
      pairs.push([this.frT("currency"), h.currency_label || h.currency || "SYP"]);
      return pairs;
    },
    infoSheet() {
      return {
        name: this.t("sheetInfo"),
        title: this.t("sheetInfo"),
        columns: [
          { key: "label", label: this.t("item"), type: "text", width: 26 },
          { key: "value", label: this.t("value"), type: "text", width: 54 },
        ],
        rows: this.buildInfoPairs().map((pair) => ({ label: pair[0], value: pair[1] })),
        headerBlock: true,
      };
    },

    // ------------------------------------------------------------------
    // Export models
    // ------------------------------------------------------------------
    buildLineColumns(showAccount) {
      const columns = [
        { key: "index", label: "#", type: "count", width: 6 },
        { key: "date", label: this.t("colDate"), type: "date", width: 12 },
        { key: "reference_no", label: this.t("colEntryNo"), type: "code", width: 16 },
      ];
      if (showAccount) {
        columns.push({ key: "account", label: this.frT("account"), type: "text", width: 30 });
      }
      return columns.concat([
        { key: "description", label: this.t("colDescription"), type: "text", width: 40 },
        { key: "reference", label: this.t("colReference"), type: "text", width: 18 },
        { key: "debit", label: this.frT("debit"), type: "money" },
        { key: "credit", label: this.frT("credit"), type: "money" },
        { key: "balance", label: this.frT("balance"), type: "money" },
        { key: "nature_label", label: this.t("colNature"), type: "text", width: 18 },
        { key: "flags_text", label: this.t("colNotes"), type: "text", width: 30 },
      ]);
    },
    lineExportRows(report) {
      const rows = [];
      const firstPage = !report.pagination || report.pagination.page === 1;
      rows.push({
        _kind: "opening",
        description: firstPage && !report.filtered ? this.t("opening") : this.t("carriedForward"),
        balance: report.page_opening_balance,
        nature_label: this.natureLabelOf(report.page_opening_balance),
      });
      (report.rows || []).forEach((row) => {
        rows.push({
          index: row.index,
          date: row.date,
          reference_no: row.reference_no || "#" + row.journal_entry_id,
          account: row.account_code + " " + (row.account_name || ""),
          description: row.description || "",
          reference: row.reference || "",
          debit: row.debit,
          credit: row.credit,
          balance: row.balance,
          nature_label: row.nature_label,
          flags_text: (row.flags || []).map((flag) => flag.text).join("، "),
          _highlight: row.is_abnormal ? "warn" : undefined,
        });
      });
      if (!(report.rows || []).length) {
        rows.push({ description: report.too_many ? this.frT("tooManyRows") : report.message || this.frT("noMovements") });
      }
      if (report.filtered && report.filtered_totals) {
        rows.push({
          _kind: "subtotal",
          description: this.t("filteredTotal") + " (" + report.filtered_totals.lines + ")",
          debit: report.filtered_totals.debit,
          credit: report.filtered_totals.credit,
        });
      }
      rows.push({
        _kind: "total",
        description: this.t("periodTotal") + " (" + report.totals.lines + " " + this.t("linesWord") + ")",
        debit: report.totals.debit,
        credit: report.totals.credit,
      });
      rows.push({
        _kind: "closing",
        description: this.t("closing"),
        balance: report.closing.balance,
        nature_label: report.closing.nature_label,
      });
      return rows;
    },
    partyAccountsSheet(report) {
      const columns = [
        { key: "code", label: this.frT("accountCode"), type: "code", width: 18 },
        { key: "name", label: this.frT("accountName"), type: "text", width: 34 },
        { key: "purpose_label", label: this.t("accountKind"), type: "text", width: 18 },
        { key: "opening", label: this.t("opening"), type: "money" },
        { key: "debit", label: this.frT("debit"), type: "money" },
        { key: "credit", label: this.frT("credit"), type: "money" },
        { key: "closing", label: this.t("closing"), type: "money" },
        { key: "nature_label", label: this.t("colNature"), type: "text", width: 18 },
        { key: "alert", label: this.t("colNotes"), type: "text", width: 24 },
      ];
      const rows = (report.accounts || []).map((a) => ({
        code: a.code,
        name: a.name,
        purpose_label: a.purpose_label + (a.is_shared ? " (" + this.t("sharedAccount") + ")" : ""),
        opening: a.opening,
        debit: a.debit,
        credit: a.credit,
        closing: a.closing,
        nature_label: a.nature_label,
        alert: a.alert ? a.alert.text : "",
        _highlight: a.is_abnormal ? "warn" : undefined,
      }));
      rows.push({
        _kind: "total",
        name: this.t("partyTotal"),
        opening: report.opening.balance,
        debit: report.totals.debit,
        credit: report.totals.credit,
        closing: report.closing.balance,
        nature_label: report.closing.nature_label,
      });
      return { name: this.t("sheetPartyAccounts"), title: this.t("sheetPartyAccounts"), columns: columns, rows: rows };
    },
    summarizedSheet(report) {
      const columns = [
        { key: "label", label: report.group_by === "week" ? this.t("week") : this.t("month"), type: "text", width: 26 },
        { key: "lines", label: this.t("movementsCount"), type: "count" },
        { key: "debit", label: this.t("totalDebit"), type: "money" },
        { key: "credit", label: this.t("totalCredit"), type: "money" },
        { key: "net", label: this.t("netMovement"), type: "money" },
        { key: "balance", label: this.frT("balance"), type: "money" },
        { key: "nature_label", label: this.t("colNature"), type: "text", width: 18 },
      ];
      const rows = [
        { _kind: "opening", label: this.t("opening") + " (" + report.header.period.from + ")", balance: report.opening.balance, nature_label: report.opening.nature_label },
      ];
      (report.buckets || []).forEach((b) => {
        rows.push({
          label: b.label + (b.provisional ? " (" + this.t("provisional") + ")" : ""),
          lines: b.lines,
          debit: b.debit,
          credit: b.credit,
          net: b.net,
          balance: b.balance,
          nature_label: b.nature_label,
          _highlight: b.is_abnormal ? "warn" : undefined,
        });
        const expanded = this.expandedBuckets[b.key];
        if (expanded && expanded.rows && expanded.rows.length) {
          expanded.rows.forEach((row) => {
            rows.push({
              label: row.date + "  " + (row.reference_no || "#" + row.journal_entry_id) + "  " + (row.description || ""),
              debit: row.debit,
              credit: row.credit,
              balance: row.balance,
              _level: 1,
            });
          });
        }
      });
      rows.push({ _kind: "total", label: this.frT("total"), lines: report.totals.lines, debit: report.totals.debit, credit: report.totals.credit, net: report.totals.net });
      rows.push({ _kind: "closing", label: this.t("closing") + " (" + report.header.period.to + ")", balance: report.closing.balance, nature_label: report.closing.nature_label });
      columns[0].indent = true;
      return { name: this.t("mode_summarized"), title: this.t("mode_summarized"), columns: columns, rows: rows };
    },
    entrySheet(report) {
      const columns = [
        { key: "index", label: "#", type: "count", width: 6 },
        { key: "account_code", label: this.frT("accountCode"), type: "code", width: 18 },
        { key: "account_name", label: this.frT("accountName"), type: "text", width: 34 },
        { key: "debit", label: this.frT("debit"), type: "money" },
        { key: "credit", label: this.frT("credit"), type: "money" },
        { key: "line_memo", label: this.t("colDescription"), type: "text", width: 36 },
      ];
      const rows = (report.lines || []).map((line) => ({
        index: line.index,
        account_code: line.account_code,
        account_name: line.account_name,
        debit: line.debit,
        credit: line.credit,
        line_memo: line.line_memo || "",
      }));
      rows.push({
        _kind: "total",
        account_name: this.frT("total"),
        debit: report.totals.debit,
        credit: report.totals.credit,
        line_memo: report.totals.balance_label,
        _highlight: report.totals.is_balanced ? "ok" : "error",
      });
      return { name: this.t("mode_entry"), title: this.t("mode_entry"), columns: columns, rows: rows };
    },
    comparativeSheet(report) {
      // Mixed money and count rows: values go out as plain display numbers
      // (money ÷100 here, counts as-is) so Excel keeps them numeric.
      const columns = [
        { key: "label", label: this.t("item"), type: "text", width: 24 },
        { key: "a", label: this.periodALabel, type: "number", width: 22 },
        { key: "b", label: this.periodBLabel, type: "number", width: 22 },
        { key: "difference", label: this.t("difference"), type: "number", width: 18 },
        { key: "change_pct", label: this.t("changePct"), type: "percent" },
      ];
      const display = (item, value) => (item.key === "lines" ? Number(value) || 0 : this.frMoney(value));
      const rows = (report.items || []).map((item) => ({
        label: item.label,
        a: display(item, item.a),
        b: display(item, item.b),
        difference: display(item, item.difference),
        change_pct: item.change_pct,
        _kind: item.key === "opening" || item.key === "closing" ? "subtotal" : undefined,
      }));
      return { name: this.t("mode_comparative"), title: this.t("mode_comparative"), columns: columns, rows: rows };
    },
    /** For detailed / party exports with more than one page, fetch every line first. */
    fullLinesReport() {
      const r = this.report;
      if (!r || !r.pagination || r.pagination.last_page <= 1) {
        return Promise.resolve(r);
      }
      this.loading = true;
      return this.axios
        .get(this.api.ACTIONS.ACCOUNTING_ACCOUNT_STATEMENT, { params: this.buildParams(this.mode, { per_page: "all", page: 1 }) })
        .then((response) => {
          const data = (response.data && response.data.data) || null;
          if (data && data.too_many) {
            window.getApp.$emit("SHOW_TOAST", this.frT("tooManyRows"), "orange");
            return null;
          }
          return data;
        })
        .catch((error) => {
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.frT("failedExport")), "red");
          return null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportModel(report) {
      const sheets = [this.infoSheet()];
      if (report.mode === "party") {
        sheets.push(this.partyAccountsSheet(report));
      }
      if (report.mode === "detailed" || report.mode === "party") {
        sheets.push({
          name: this.t("sheetLines"),
          title: this.t("sheetLines"),
          columns: this.buildLineColumns(report.mode === "party" || !!(report.account && report.account.include_children)),
          rows: this.lineExportRows(report),
        });
      } else if (report.mode === "summarized") {
        sheets.push(this.summarizedSheet(report));
      } else if (report.mode === "entry") {
        sheets.push(this.entrySheet(report));
      } else if (report.mode === "comparative") {
        sheets.push(this.comparativeSheet(report));
      }
      const notes = []
        .concat((report.alerts || []).map((a) => a.text))
        .concat((report.warnings || []).map((w) => w.text))
        .concat(report.provisional && report.provisional.is_provisional ? [report.provisional.text] : [])
        .concat(report.reconciliation ? [this.reconciliationTitle(report.reconciliation)] : [])
        .concat(report.notes || []);
      sheets[sheets.length - 1].notes = notes;
      return { sheets: sheets, notes: notes };
    },
    exportExcel() {
      this.fullLinesReport().then((report) => {
        if (!report) return;
        const model = this.exportModel(report);
        this.frExportXlsx({
          fileName: "account-statement-" + report.mode,
          header: report.header,
          sheets: model.sheets,
        });
      });
    },
    exportPrint() {
      this.fullLinesReport().then((report) => {
        if (!report) return;
        const model = this.exportModel(report);
        const sections = model.sheets.map((sheet, index) => ({
          title: sheet.title,
          columns: sheet.columns,
          rows: sheet.rows,
          notes: index === model.sheets.length - 1 ? sheet.notes : [],
        }));
        const wide = report.mode === "detailed" || report.mode === "party";
        this.frPrint({ header: report.header, sections: sections, orientation: wide ? "landscape" : "portrait" });
      });
    },
    downloadCsv() {
      const params = this.buildParams(this.mode, this.usesLines ? { per_page: "all", page: 1 } : {});
      this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_ACCOUNT_STATEMENT, params, "account-statement-" + this.mode);
    },
  },
};
</script>

<style scoped>
.as-modes {
  margin-inline-start: 16px;
}
.as-check {
  flex: 0 0 auto;
}
.as-narrow {
  max-width: 120px;
}
.as-hint {
  padding: 32px;
  text-align: center;
  color: rgba(0, 0, 0, 0.55);
}
.as-info {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 22px;
  padding: 10px 14px 4px;
  font-size: 13px;
}
.as-info-label {
  color: rgba(0, 0, 0, 0.55);
  margin-inline-end: 6px;
}
.as-info-value {
  font-weight: 600;
}
.as-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 10px;
}
.as-alert,
.as-warning {
  margin: 4px 12px;
}
.as-warning {
  color: #bf360c;
  font-size: 13px;
}
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.as-table-wrap {
  --fin-table-max-h: calc(100vh - 300px);
  min-height: 160px;
}
.as-table-wrap--short {
  --fin-table-max-h: none;
  min-height: 0;
}
.as-table th,
.as-table td {
  padding: 5px 8px;
  vertical-align: top;
}
/* The drill-down table sits inside a row of the outer one: no sticky. */
.as-inner {
  font-size: 12px;
  margin: 4px 0;
}
.as-inner thead th,
.as-inner tfoot {
  position: static !important;
}
.as-nowrap {
  white-space: nowrap;
}
.as-desc {
  min-width: 220px;
  white-space: normal !important;
}
.as-link {
  cursor: pointer;
  text-decoration: underline;
}
.as-opening td,
.as-closing td,
.as-subtotal td {
  background: #f5f5f5;
  font-weight: 700;
}
.as-total td {
  background: #e8f5e9;
  font-weight: 700;
}
.as-total.as-bad td {
  background: #ffcdd2;
}
.as-abnormal td {
  background: #fff3e0;
}
.as-reversal td {
  color: #6a1b9a;
}
.as-bucket-empty td {
  color: rgba(0, 0, 0, 0.45);
}
.as-drill-row > td {
  background: #fafafa;
}
.as-toggle-col {
  width: 34px;
}
.as-toggle {
  margin: 0;
  height: 22px;
  width: 22px;
}
.as-side {
  font-weight: 600;
  white-space: nowrap;
}
.as-side-debit {
  color: #1565c0;
}
.as-side-credit {
  color: #2e7d32;
}
.as-side-balanced {
  color: rgba(0, 0, 0, 0.55);
}
.as-flags {
  min-width: 140px;
}
.as-flag {
  display: inline-block;
  font-size: 11px;
  line-height: 16px;
  padding: 0 6px;
  margin: 1px 2px;
  border-radius: 8px;
  background: #eceff1;
  color: #37474f;
  white-space: nowrap;
}
.as-flag-reversal,
.as-flag-reversed {
  background: #f3e5f5;
  color: #6a1b9a;
}
.as-flag-provisional {
  background: #fff8e1;
  color: #8d6e00;
}
.as-flag-large_amount,
.as-flag-future_date,
.as-flag-very_old {
  background: #ffe0b2;
  color: #bf360c;
}
.as-muted {
  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
}
.as-pager {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 12px 10px;
}
.as-notes {
  padding: 6px 14px 14px;
}
.as-note {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 2px 0;
}
@media print {
  .fr-no-print {
    display: none !important;
  }
}
</style>
